import type { CardCourseSlot, CardStack } from "@shared/models/card-stack"
import type { Planbook, PlanbookCalendarDateCustom } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { objectId } from "@shared/util/object-id"
import { compact, find, forEach, map, pull, reject } from "lodash-es"
import { filterPlanbookCustomDates, filterPlanbookDefaultDates } from "../filter-dates/planbook"
import { filterForRelevantDates, pluckNewRotationIds } from "../filter-dates/rotation-calendar"
import type { CustomDate } from "../filter-dates/shared"
import { filterForDatesBefore, findDate } from "../filter-dates/shared"

export function updatePlanbookToRotationCalendar(
  sourceRotationCalendar: RotationCalendar,
  destinationRotationCalendar: RotationCalendar,
  planbook: Planbook
) {
  let effectiveDate = sourceRotationCalendar.attributes.datesLastChangedOn
  if (effectiveDate === null || effectiveDate === undefined || typeof effectiveDate !== "string") {
    throw new Error("Old Rotation Calendar needs a `datesLastChangedOn` Property")
  }

  /**
   * ---------------------------------------------------------------------------------
   * Pluck all the cardStackIds with their rotationId so we can shift them
   * ---------------------------------------------------------------------------------
   */

  let newCustomDates = generateCustomDates({
    sourceRotationCalendar,
    destinationRotationCalendar,
    effectiveDate,
    customDates: filterPlanbookCustomDates(planbook),
  })

  let newPlanbookDates: Array<PlanbookCalendarDateCustom> = map(newCustomDates, (date) => {
    return {
      id: date.dateString,
      type: "planbook-date-custom",
      attributes: {
        cardStackId: date.cardStackId,
        dateString: date.dateString,
      },
    }
  })

  // Handle unclaimed cardStackIdsArray
  // if (cardStackIdsArray.length > 0) {
  //   // We have to go to the end of the school year and append I think.
  //   forEach(cardStackIdsArray, hash => {})
  // }

  /**
   * --------------------------------------------------------------------------------
   * Generate new card stacks as needed
   * --------------------------------------------------------------------------------
   */

  /**
   * Pluck default dates and add as necessary in the next step
   */
  let defaultDates = filterPlanbookDefaultDates(planbook)

  /**
   * Ids of Rotations we don't have yet in this planbook yet
   */
  let newRotationIds = pluckNewRotationIds(sourceRotationCalendar, defaultDates)

  /**
   * The new cards stacks we need to generate for routine templates
   */
  let newCardStacks: Array<CardStack> = []

  if (newRotationIds.length > 0) {
    let semesterId = sourceRotationCalendar.attributes.semesters[0].id
    forEach(newRotationIds, (rotationId) => {
      // We'll use this in the planbook-date-default and the card stack
      let cardStackId = objectId()

      // Push in the planbook-date-default to the planbook
      defaultDates.push({
        id: `${semesterId}:${rotationId}`,
        type: "planbook-date-default",
        attributes: {
          cardStackId: cardStackId,
          rotationId: rotationId,
          semesterId: semesterId,
        },
      })

      // Generate the new card stack
      newCardStacks.push({
        id: cardStackId,
        type: "card-stack",
        attributes: {
          title: null,
          color: null,
          schemaVersion: 1,
          usedAs: "routine-template",
          _revisedBySessionId: null,
          _revision: 0,
          version: 0,
          commentIds: [],
          cards: map(planbook.relationships.courses.data, (datum, index): CardCourseSlot => {
            return {
              id: objectId(),
              type: "card-course-slot",
              attributes: {
                courseId: datum.id,
                position: index * 1000,
                parentCardId: null,
              },
            }
          }),
          proseMirror: null,
          extracted: null,
        },
        meta: {
          permissions: planbook.meta.permissions,
        },
        relationships: {
          course: { data: null },
          planbook: {
            data: {
              id: planbook.id,
              type: "planbook",
            },
          },
        },
      })
    })
  }

  /**
   * --------------------------------------------------------------------------------
   * Combine them all together
   * --------------------------------------------------------------------------------
   */

  /**
   * Pluck out the custom dates that didn't change
   */
  let customDatesToKeep = filterForDatesBefore(filterPlanbookCustomDates(planbook), effectiveDate)

  return {
    dates: [...defaultDates, ...customDatesToKeep, ...newPlanbookDates],
    newCardStacks: newCardStacks,
  }
}

function generateCustomDates<T extends CustomDate>({
  sourceRotationCalendar,
  destinationRotationCalendar,
  effectiveDate,
  customDates,
}: {
  sourceRotationCalendar: RotationCalendar
  destinationRotationCalendar: RotationCalendar
  effectiveDate: string
  customDates: Array<T>
}): Array<{ dateString: string; cardStackId: string | null }> {
  /**
   * ---------------------------------------------------------------------------------
   * Pluck all the cardStackIds with their rotationId so we can shift them
   * ---------------------------------------------------------------------------------
   */

  /**
   * The dates in the destination calendar on and after the effective date
   */

  let relevantSourceDates = filterForRelevantDates(sourceRotationCalendar, effectiveDate)
  let relevantDestinationDates = filterForRelevantDates(destinationRotationCalendar, effectiveDate)
  /**
   * An array of maps of that have all the cardStackId and what rotation they were originally on
   * We keep it in an array so we can reapply them in order if we run out of rotations. A previous
   * implementation was a map: {a: CardStackId[], b: CardStackId[]}, but if we needed to lay down
   * the card stacks ids on days the rotation calendar didn't cover, we had lost their original order.
   */
  let cardStackIdsArray = compact(
    map(relevantSourceDates, (date) => {
      if (date.attributes.rotationId === null) return null
      let customDate = findDate(customDates, date.attributes.dateString)
      return {
        rotationId: date.attributes.rotationId,
        cardStackId: customDate ? customDate.attributes.cardStackId : null,
      }
    })
  )

  /**
   * ------------------------------------------------------------------
   * Create new planbook dates, using the routineIds we extracted above
   * ------------------------------------------------------------------
   */
  /**
   * Planbook dates that we need to add to the planbook as a result of the rotation calendar
   * changing.
   */
  let newCustomDates = compact(
    map(
      relevantDestinationDates,
      (
        rotationDate
      ): {
        cardStackId: string | null
        dateString: string
      } | null => {
        if (rotationDate.attributes.rotationId === null) return null

        // Find a relevant id. This is a bit confusing, but the first branch is for courses
        // where you want to move a lesson to all the available rotations. E.g. a course that is on A annd B days
        // the second fork is for planbooks where you just want to make sure A days move to A days, etc
        let customDate = find(
          cardStackIdsArray,
          (hash) => hash.rotationId === rotationDate.attributes.rotationId
        )

        pull(cardStackIdsArray, customDate) // remove the element we just removed from the array so we don't reapply it

        let cardStackId = customDate?.cardStackId || null

        return {
          dateString: rotationDate.attributes.dateString,
          cardStackId: cardStackId,
        }
      }
    )
  )

  // filter out any remain cardStackIds that are null
  cardStackIdsArray = reject(cardStackIdsArray, (hash) => hash.cardStackId === null)

  return newCustomDates
}
